import { Link } from "react-router-dom";

interface PricingFeature {
  label: string;
  included: boolean;
}

interface PricingTier {
  name: string;
  price: number;
  pricePhp: number;
  credits: number;
  orgs: string;
  storage: string;
  storageOverage: string | null;
  seats: string;
  interviews: string;
  features: PricingFeature[];
  support: string;
  highlighted: boolean;
  badge: string | null;
}

const TIERS: PricingTier[] = [
  {
    name: "Starter",
    price: 19,
    pricePhp: 1064,
    credits: 1000,
    orgs: "1 organization",
    storage: "1 GB",
    storageOverage: null,
    seats: "5 seats",
    interviews: "10/day",
    features: [
      { label: "Resume Screening", included: true },
      { label: "Intake Analysis", included: true },
      { label: "Endorsement Forms", included: false },
      { label: "Calendar", included: false },
      { label: "Profile Archive", included: false },
      { label: "Pipeline Automation", included: false },
      { label: "Salary Benchmark", included: false },
      { label: "CV Formatting", included: false },
      { label: "Custom Roles & Permissions", included: false },
    ],
    support: "Email support",
    highlighted: false,
    badge: null,
  },
  {
    name: "Premium",
    price: 59,
    pricePhp: 3304,
    credits: 5000,
    orgs: "5 organizations",
    storage: "10 GB",
    storageOverage: "$1.00/GB overage",
    seats: "15 seats",
    interviews: "1,000/day",
    features: [
      { label: "Resume Screening", included: true },
      { label: "Intake Analysis", included: true },
      { label: "Endorsement Forms", included: true },
      { label: "Calendar", included: true },
      { label: "Profile Archive", included: true },
      { label: "Pipeline Automation", included: false },
      { label: "Salary Benchmark", included: false },
      { label: "CV Formatting", included: false },
      { label: "Custom Roles & Permissions", included: false },
    ],
    support: "Priority email support",
    highlighted: true,
    badge: "Most Popular",
  },
  {
    name: "Enterprise",
    price: 179,
    pricePhp: 10024,
    credits: 20000,
    orgs: "100 organizations",
    storage: "50 GB",
    storageOverage: "$0.50/GB overage",
    seats: "1,000 seats",
    interviews: "Unlimited",
    features: [
      { label: "Resume Screening", included: true },
      { label: "Intake Analysis", included: true },
      { label: "Endorsement Forms", included: true },
      { label: "Calendar", included: true },
      { label: "Profile Archive", included: true },
      { label: "Pipeline Automation", included: true },
      { label: "Salary Benchmark", included: true },
      { label: "CV Formatting", included: true },
      { label: "Custom Roles & Permissions", included: true },
    ],
    support: "Dedicated support",
    highlighted: false,
    badge: null,
  },
];

function CheckIcon() {
  return (
    <svg
      className="h-4 w-4 text-emerald-500 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      className="h-4 w-4 text-gray-300 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function PricingCard({ tier }: { tier: PricingTier }) {
  const cardClass = tier.highlighted
    ? "relative rounded-card p-8 ring-2 ring-rausch bg-near-black text-white shadow-2xl scale-[1.03]"
    : "relative rounded-card p-8 bg-white shadow-card";

  const priceClass = tier.highlighted ? "text-white" : "text-near-black";
  const subtextClass = tier.highlighted
    ? "text-white/60"
    : "text-text-secondary";
  const labelClass = tier.highlighted ? "text-white/70" : "text-text-secondary";
  const featureTextClass = tier.highlighted
    ? "text-white/80"
    : "text-near-black";
  const dividerClass = tier.highlighted
    ? "border-white/10"
    : "border-border-card";

  return (
    <div className={cardClass}>
      {tier.badge && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span className="inline-flex rounded-badge bg-rausch px-3 py-1 text-[11px] font-bold text-white uppercase tracking-wider shadow">
            {tier.badge}
          </span>
        </div>
      )}

      <div className="mb-6">
        <p
          className={`text-xs font-bold uppercase tracking-[0.32px] mb-1 ${labelClass}`}
        >
          {tier.name}
        </p>
        <div className="flex items-end gap-1">
          <span className={`text-5xl font-bold ${priceClass}`}>
            ${tier.price}
          </span>
          <span className={`text-base mb-2 ${subtextClass}`}>/mo</span>
        </div>
        <p className={`text-xs mt-1 ${subtextClass}`}>
          ~₱{tier.pricePhp.toLocaleString()}/mo
        </p>
      </div>

      <div className={`space-y-2 pb-6 border-b ${dividerClass}`}>
        <div className={`flex justify-between text-sm`}>
          <span className={subtextClass}>Credits/month</span>
          <span className={`font-semibold ${priceClass}`}>
            {tier.credits.toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className={subtextClass}>Organizations</span>
          <span className={`font-semibold ${priceClass}`}>{tier.orgs}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className={subtextClass}>Storage</span>
          <span className={`font-semibold ${priceClass}`}>{tier.storage}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className={subtextClass}>Extra storage</span>
          <span className={`font-semibold ${priceClass}`}>
            {tier.storageOverage ?? "—"}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className={subtextClass}>User seats</span>
          <span className={`font-semibold ${priceClass}`}>{tier.seats}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className={subtextClass}>Schedule interviews</span>
          <span className={`font-semibold ${priceClass}`}>{tier.interviews}</span>
        </div>
      </div>

      <ul className="mt-6 space-y-3">
        {tier.features.map((f) => (
          <li key={f.label} className="flex items-center gap-3">
            {f.included ? <CheckIcon /> : <XIcon />}
            <span
              className={`text-sm ${f.included ? featureTextClass : subtextClass}`}
            >
              {f.label}
            </span>
          </li>
        ))}
        <li className="flex items-center gap-3">
          <CheckIcon />
          <span className={`text-sm ${featureTextClass}`}>{tier.support}</span>
        </li>
      </ul>

      <Link
        to="/signup"
        className={`mt-8 block w-full rounded-btn py-3 text-sm font-semibold text-center transition-colors ${
          tier.highlighted
            ? "bg-rausch text-white hover:bg-[#e0334f]"
            : "bg-near-black text-white hover:bg-rausch"
        }`}
      >
        Get Started
      </Link>
    </div>
  );
}

export function LandingPricing() {
  return (
    <section id="pricing" className="bg-surface-secondary py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-[40px] font-bold text-near-black leading-tight tracking-tight">
            Simple, transparent pricing.
          </h2>
          <p className="mt-4 text-lg text-text-secondary">
            Credits-based billing — you only pay for what you use. Upgrade or
            downgrade anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 items-center max-w-5xl mx-auto">
          {TIERS.map((tier) => (
            <PricingCard key={tier.name} tier={tier} />
          ))}
        </div>
      </div>
    </section>
  );
}
